import React, { useMemo, useState } from "react";

import { type GetCustomMetrics_me_customMetrics } from "@north-beam/nb-client-side";
import { type AccountingMode } from "@north-beam/nb-common";
import { TextInput } from "@shared/text-input";

import { useHasSubmittedMetric, useSelectedIsDirtyMetric } from "../atoms";
import { FormulaInput } from "../custom-metric-inputs";
import { DataTypeSelect } from "../custom-metric-inputs/data-type-select";
import { AccountingModeSelector } from "../custom-metric-inputs/metric-selector/accounting-mode-selector";
import { ConfirmAccountingMode } from "../custom-metric-inputs/metric-selector/metric-selector";
import { NegativeTrendCheckbox } from "../custom-metric-inputs/negative-trend-checkbox";
import { useCurrentlyUpdatingMetric } from "../hooks/use-currently-updating-metric";
import { useCustomMetrics } from "../hooks/use-custom-metrics";
import { useDisabledControls } from "../hooks/use-disabled-controls";
import { useFormulaBuilder } from "../hooks/use-formula-builder";
import { useFormulaHistory } from "../hooks/use-formula-history";

import { NoMetricsStateInput } from "./no-metrics-state-input";

export const useNameError = (
  name: string,
  customMetrics: GetCustomMetrics_me_customMetrics[],
  selectedMetric: GetCustomMetrics_me_customMetrics | null,
) => {
  const nameErrorMessage = useMemo(() => {
    const trimmedExisting = customMetrics
      .map(({ name: metricName }) => metricName.trim())
      .filter((i) => i !== selectedMetric?.name?.trim());

    if (trimmedExisting.find((v) => v === name.trim())) {
      return "Metric name's must be unique";
    }

    return undefined;
  }, [customMetrics, name, selectedMetric?.name]);

  return nameErrorMessage;
};

export const FormulaBuilder = () => {
  const { customMetrics, selectedMetric } = useCustomMetrics();
  const isDisabled = useDisabledControls();
  const [hasSubmitted] = useHasSubmittedMetric();

  const {
    name,
    setName,
    description,
    setDescription,
    dataType,
    setDataType,
    isNegativeTrendGood,
    setIsNegativeTrendGood,
    currentlyUpdatingMetric,
  } = useCurrentlyUpdatingMetric();

  const nameErrorMessage = useNameError(name, customMetrics, selectedMetric);

  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const { accountingMode, setAccountingMode } = useCurrentlyUpdatingMetric();
  const [pendingAccountingMode, setPendingAccountingMode] =
    useState(accountingMode);
  const { clearHistory } = useFormulaHistory();
  const { clear } = useFormulaBuilder();

  const confirmChangingAccountingMode = (mode: AccountingMode) => {
    if (accountingMode !== mode) {
      setIsConfirmingClear(true);
      setPendingAccountingMode(mode);
    }
  };
  const confirmAccountingMode = () => {
    setAccountingMode(pendingAccountingMode);
    clearHistory();
    clear();
    setIsConfirmingClear(false);
  };

  if (!currentlyUpdatingMetric) {
    return <NoMetricsStateInput />;
  }

  return (
    <div className="[grid-area:formula-input]">
      {currentlyUpdatingMetric.isTemplate && (
        <div className="flex text-sm mb-2">
          <div className="bg-[#36B7B7] rounded-s-lg flex items-center justify-center w-9">
            <i className="fa-regular fa-sharp fa-info-circle text-white" />
          </div>
          <div className="flex-1 p-2 pr-3 bg-[#EDFBFB] rounded-e-lg">
            This is a template metric. Templates cannot be updated or deleted
          </div>
        </div>
      )}
      <div className="flex items-start space-x-4 mb-2 ">
        <TextInput
          className="w-unset mb-2 text-sm"
          isDisabled={isDisabled}
          label="NAME"
          value={name}
          placeholder="Name..."
          errorMessage={nameErrorMessage}
          onChange={(e) => {
            const changed = e.target.value;
            setName(changed);
          }}
        />
        <DataTypeSelect
          dataType={dataType}
          setDataType={setDataType}
          isDisabled={isDisabled}
        />
        {isConfirmingClear ? (
          <ConfirmAccountingMode
            confirmAccountingMode={confirmAccountingMode}
            setIsConfirmingClear={setIsConfirmingClear}
          />
        ) : (
          <AccountingModeSelector
            isDisabled={isDisabled}
            accountingMode={accountingMode}
            confirmChangingAccountingMode={confirmChangingAccountingMode}
          />
        )}
      </div>
      <TextInput
        className="w-full mb-4 text-sm"
        label="DESCRIPTION"
        isDisabled={isDisabled}
        value={description}
        placeholder="Description..."
        onChange={(e) => setDescription(e.target.value)}
        errorMessage={
          hasSubmitted && !description ? "Description is required" : ""
        }
      />
      <FormulaInput isDisabled={isDisabled} />
      <NegativeTrendCheckbox
        value={isNegativeTrendGood}
        onChange={setIsNegativeTrendGood}
        isDisabled={isDisabled}
      />
    </div>
  );
};
